import usePageTitle from '../hooks/usePageTitle';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, AlertCircle, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { getImageUrl } from '../lib/api';

const DEFAULT_MINIMUM = 3;
const CATEGORY_MINIMUMS = {
  'caps': 6,
  'bags': 6,
};

const getMinimumForCategory = (category) => {
  return CATEGORY_MINIMUMS[category?.toLowerCase()] || DEFAULT_MINIMUM;
};

const CartPage = () => {
  usePageTitle('Cart');
  const { cart, updateCartItem, removeFromCart, loading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleQuantityChange = async (productId, sizes, size, delta) => {
    const newSizes = { ...sizes };
    newSizes[size] = Math.max(0, (newSizes[size] || 0) + delta);
    
    // Remove size if 0
    if (newSizes[size] === 0) {
      delete newSizes[size];
    }

    try {
      if (Object.keys(newSizes).length === 0) {
        await removeFromCart(productId);
        toast.success('Item removed from cart');
      } else {
        await updateCartItem(productId, newSizes);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
    }
  };

  const handleInputChange = async (productId, sizes, size, value) => {
    const newQty = parseInt(value) || 0;
    const newSizes = { ...sizes };
    
    if (newQty <= 0) {
      delete newSizes[size];
    } else {
      newSizes[size] = newQty;
    }

    try {
      if (Object.keys(newSizes).length === 0) {
        await removeFromCart(productId);
        toast.success('Item removed from cart');
      } else {
        await updateCartItem(productId, newSizes);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
    }
  };

  const handleRemoveItem = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const creditAvailable = (user?.credit_limit || 0) - (user?.credit_used || 0);
  const totalWithGst = cart.total * 1.10; // 10% GST
  const canCheckout = totalWithGst <= creditAvailable;
  const totalUnits = cart.items.reduce((acc, item) => acc + item.total_quantity, 0);

  // Check for minimum quantity violations (category-specific)
  const minimumViolations = [];
  cart.items.forEach(item => {
    const category = item.product_category || '';
    const minQty = getMinimumForCategory(category);
    Object.entries(item.sizes).forEach(([size, qty]) => {
      if (qty > 0 && qty < minQty) {
        minimumViolations.push({
          product: item.product_name,
          size,
          qty,
          needed: minQty - qty,
          minimum: minQty,
          category: category
        });
      }
    });
  });

  // Group cart items by category
  const groupedItems = cart.items.reduce((acc, item) => {
    const category = item.product_category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // Sort categories alphabetically, but put 'uncategorized' last
  const sortedCategories = Object.keys(groupedItems).sort((a, b) => {
    if (a === 'uncategorized') return 1;
    if (b === 'uncategorized') return -1;
    return a.localeCompare(b);
  });
  
  // Format category name for display
  const formatCategoryName = (category) => {
    if (category === 'uncategorized') return 'Other Items';
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (cart.items.length === 0) {
    return (
      <div className="container-wide py-16 text-center fade-in">
        <ShoppingBag className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Your Cart is Empty</h1>
        <p className="text-[#64748B] mb-8">Add some products to get started</p>
        <Link to="/products">
          <Button className="bg-[#002FA7] hover:bg-[#0025a0]" data-testid="continue-shopping-btn">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-[#0F172A]">Shopping Cart</h1>
        <span className="text-[#64748B]">{cart.items.length} items • {totalUnits} units</span>
      </div>

      {/* Minimum Order Warning */}
      {minimumViolations.length > 0 && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B] p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-[#92400E] mb-2">Minimum 3 units required per size</p>
              <ul className="text-sm text-[#92400E] space-y-1">
                {minimumViolations.map((v, i) => (
                  <li key={i}>
                    {v.product} - Size {v.size}: needs {v.needed} more unit{v.needed > 1 ? 's' : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items - Grouped by Category */}
        <div className="lg:col-span-2 space-y-6">
          {sortedCategories.map((category) => (
            <div key={category} className="border border-[#E2E8F0] overflow-hidden">
              {/* Category Header */}
              <div className="bg-[#002FA7] text-white px-4 py-2 flex items-center justify-between">
                <span className="font-semibold">{formatCategoryName(category)}</span>
                <span className="text-sm text-white/80">
                  {groupedItems[category].length} item{groupedItems[category].length > 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-[#F8FAFC]">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-[#0F172A]">Product</th>
                      <th className="text-center p-4 text-sm font-semibold text-[#0F172A]">Price</th>
                      <th className="text-center p-4 text-sm font-semibold text-[#0F172A]">Sizes & Quantities</th>
                      <th className="text-right p-4 text-sm font-semibold text-[#0F172A]">Subtotal</th>
                      <th className="p-4 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {groupedItems[category].map((item) => (
                      <tr key={item.product_id} className="hover:bg-[#FAFAFA]" data-testid={`cart-item-${item.product_id}`}>
                        {/* Product with Image */}
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Product Image */}
                        <div className="w-16 h-16 bg-[#F8FAFC] rounded overflow-hidden flex-shrink-0">
                          <img
                            src={getImageUrl(item.product_image) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=100'}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Product Details */}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link
                              to={`/products/${item.product_id}`}
                              className="font-semibold text-[#0F172A] hover:text-[#002FA7]"
                            >
                              {item.product_name}
                            </Link>
                            {item.is_on_sale && (
                              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
                                {item.sale_type === 'custom' 
                                  ? item.sale_type_custom?.toUpperCase() 
                                  : item.sale_type?.replace(/_/g, ' ').toUpperCase() || 'SALE'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-[#64748B] font-mono mt-1">{item.product_sku}</p>
                          {/* Backorder indicator */}
                          {item.has_backorder && (
                            <div className="mt-1 flex items-center gap-1">
                              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                                Backorder
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Price */}
                    <td className="p-4 text-center">
                      {item.is_on_sale && item.original_price ? (
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-[#64748B] line-through">${item.original_price.toFixed(2)}</span>
                          <span className="font-bold text-red-600">${item.price.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="font-medium text-[#0F172A]">${item.price.toFixed(2)}</span>
                      )}
                    </td>

                    {/* Sizes */}
                    <td className="p-4">
                      <div className="flex flex-wrap justify-center gap-2">
                        {Object.entries(item.sizes).map(([size, qty]) => {
                          const isViolation = qty > 0 && qty < 3;
                          const backorderInfo = item.backorder_info?.[size];
                          const isBackorder = !!backorderInfo;
                          return (
                            <div 
                              key={size} 
                              className={`flex flex-col items-center gap-1 p-2 rounded ${
                                isViolation ? 'bg-red-50 border border-red-200' : 
                                isBackorder ? 'bg-amber-50 border border-amber-200' : 'bg-[#F8FAFC]'
                              }`}
                            >
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium text-[#64748B] w-6">{size}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuantityChange(item.product_id, item.sizes, size, -1)}
                                  disabled={loading}
                                  className="h-6 w-6 p-0"
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <Input
                                  type="number"
                                  min="0"
                                  value={qty}
                                  onChange={(e) => handleInputChange(item.product_id, item.sizes, size, e.target.value)}
                                  disabled={loading}
                                  className={`w-12 h-6 text-center text-sm p-1 ${isViolation ? 'border-red-300' : ''}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuantityChange(item.product_id, item.sizes, size, 1)}
                                  disabled={loading}
                                  className="h-6 w-6 p-0"
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                              {isBackorder && (
                                <span className="text-[10px] text-amber-600">
                                  ETA: {new Date(backorderInfo.eta).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-center text-[#64748B] mt-2">
                        {item.total_quantity} units total
                      </p>
                    </td>

                    {/* Subtotal */}
                    <td className="p-4 text-right">
                      <span className="font-bold text-[#0F172A]">${item.subtotal.toFixed(2)}</span>
                    </td>

                    {/* Remove */}
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(item.product_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Quick Order Link */}
          <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-between">
            <div>
              <p className="font-medium text-[#0F172A]">Need to add more products?</p>
              <p className="text-sm text-[#64748B]">Use Quick Order to add multiple products at once</p>
            </div>
            <Link to="/quick-order">
              <Button variant="outline">Quick Order</Button>
            </Link>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="border border-[#E2E8F0] p-6 sticky top-24">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-6">Order Summary</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-[#64748B]">Products</span>
                <span className="text-[#0F172A]">{cart.items.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#64748B]">Total Units</span>
                <span className="text-[#0F172A]">{totalUnits}</span>
              </div>
              <div className="border-t border-[#E2E8F0] pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748B]">Subtotal</span>
                  <span className="text-[#0F172A]">${cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748B]">GST (10%)</span>
                  <span className="text-[#0F172A]">${(cart.total * 0.10).toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#E2E8F0]">
                  <span className="font-semibold text-[#0F172A]">Total (inc GST)</span>
                  <span className="text-2xl font-bold text-[#002FA7]" data-testid="cart-total">
                    ${(cart.total * 1.10).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Credit Info */}
            <div className="p-4 bg-[#F8FAFC] mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#64748B]">Credit Limit</span>
                <span className="text-[#0F172A]">${(user?.credit_limit || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#64748B]">Credit Used</span>
                <span className="text-[#0F172A]">${(user?.credit_used || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold pt-2 border-t border-[#E2E8F0]">
                <span className="text-[#0F172A]">Available</span>
                <span className={creditAvailable < (cart.total * 1.10) ? 'text-red-500' : 'text-green-600'}>
                  ${creditAvailable.toFixed(2)}
                </span>
              </div>
            </div>

            {!canCheckout && (
              <div className="p-4 bg-[#FEE2E2] border border-[#FECACA] text-sm text-red-700 mb-6">
                Order total (inc GST) exceeds available credit. Please contact admin to increase your credit limit.
              </div>
            )}

            {minimumViolations.length > 0 && (
              <div className="p-4 bg-[#FEF3C7] border border-[#F59E0B] text-sm text-[#92400E] mb-6">
                Fix minimum quantities (3 per size) before checkout.
              </div>
            )}

            <Button
              onClick={() => navigate('/checkout')}
              disabled={!canCheckout || loading || minimumViolations.length > 0}
              className="w-full h-12 bg-[#002FA7] hover:bg-[#0025a0] font-semibold"
              data-testid="checkout-btn"
            >
              Proceed to Checkout
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Link to="/products">
              <Button variant="outline" className="w-full h-12 mt-3">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
